import { Component, OnInit, OnDestroy, ChangeDetectorRef, HostListener, effect, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { Router, NavigationEnd, RouterLink, RouterOutlet, ActivatedRoute } from '@angular/router';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { filter } from 'rxjs/operators';
import { AuthService } from './core/auth/auth.service';
import { NotificationService } from './core/notifications/notification.service';
import { AlertaService } from './core/notifications/alerta.service';
import { alertaTipoLabel } from './core/notifications/alerta-tipo-labels';
import { UploadTrackingService, DownloadOrigin, VerifiedOrphan } from './core/services/upload-tracking.service';
import { environment } from '../environments/environment';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { ModalComponent } from './shared/components/modal/modal.component';
import { ConfiguracionService } from './core/services/configuracion.service';
import { ConfiguracionSistemaFormComponent } from './shared/components/configuracion-sistema-form/configuracion-sistema-form.component';
import { AppSpinnerComponent } from './shared/components/spinner/spinner.component';
import { PeriodoPendienteService } from './core/services/periodo-pendiente.service';
import { TiempoActivoService } from './core/services/tiempo-activo.service';
import { EstadoPendientePeriodo } from './models/bancario.model';

/**
 * Componente raíz: el "shell" de Memo-Nova (menú lateral, cabecera, campana de alertas y panel de subidas).
 *
 * Además de pintar el layout, orquesta lo que debe pasar en cualquier pantalla: vigila la salud del
 * backend y avisa al perder o recuperar la conexión, arranca el polling de alertas, detecta subidas
 * interrumpidas por una recarga y fuerza dos modales globales: el onboarding de la configuración del
 * sistema (mes de inicio y saldo inicial, solo ADMIN) y el modal obligatorio para asignar el mes a
 * estados de cuenta cuyo periodo no se pudo determinar. El estado vive en propiedades planas, así que
 * en modo zoneless se fuerza la detección de cambios con `ChangeDetectorRef` tras cada respuesta.
 */
@Component({
    selector: 'app-root',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterLink, RouterOutlet, NzIconModule, NzLayoutModule, NzMenuModule, NzTooltipModule, ModalComponent, ConfiguracionSistemaFormComponent, AppSpinnerComponent],
    templateUrl: './app.html',
    styleUrl: './app.css'
})
export class App implements OnInit, OnDestroy {
    /** Si el menú lateral está colapsado. */
    isCollapsed = false;
    /** Si la ruta actual se muestra sin shell (login y primer login a página completa). */
    isLoginPage = false;
    /** Si el build es de producción; oculta en el menú las herramientas de desarrollo. */
    isProduction = environment.production;
    /** Título de la página tomado de `data.title` de la ruta activa más profunda. */
    pageTitle = '';
    /** Fecha mostrada en la cabecera; se fija al crear el componente. */
    currentDate = new Date();
    /** Nombre corto del usuario en sesión (sin dominio si es un correo). */
    username = '';
    /** Si el desplegable de notificaciones está abierto. */
    showNotifications = false;
    /** Si el panel de subidas en curso está abierto. */
    showUploadPanel = false;
    /** Si se muestra el modal de confirmación de cierre de sesión. */
    showLogoutConfirm = false;
    /** Subidas que una recarga dejó a medias, ya verificadas contra el backend. */
    orphanedUploads: VerifiedOrphan[] = [];

    // Onboarding de la configuración global (solo ADMIN, si aún no está configurada).
    /** Si se muestra el modal de onboarding de la configuración del sistema. */
    showConfigSetup = false;
    /** Si se está guardando la configuración del onboarding. */
    savingConfig = false;
    /** Mes de inicio de operación propuesto en el onboarding (1-12). */
    cfgMes = 1;
    /** Año de inicio de operación propuesto en el onboarding. */
    cfgAnio = new Date().getFullYear();
    /** Saldo inicial del sistema propuesto en el onboarding. */
    cfgSaldo = 0;
    /** Evita repetir la consulta de configuración más de una vez por sesión. */
    private configChecked = false;

    // Estados de cuenta sin mes determinable: modal OBLIGATORIO (no cerrable).
    // Sin flag por sesión: el pendiente vive en el servidor y se reevalúa en
    // cada recarga y tras cada subida de estados de cuenta.
    /** Si se muestra el modal obligatorio para definir el periodo de estados de cuenta. */
    showPeriodoModal = false;
    /** Estados de cuenta cuyo mes no se pudo determinar al ingerirlos. */
    pendientesPeriodo: EstadoPendientePeriodo[] = [];
    /** Mes y año elegidos por el usuario, indexados por `estado_cuenta_id`. */
    periodoSel: Record<number, { mes: number | null; anio: number | null }> = {};
    /** Guardado en curso por `estado_cuenta_id`, para deshabilitar su botón. */
    savingPeriodo: Record<number, boolean> = {};
    /** Opciones de mes del selector de periodo. */
    readonly mesesPeriodo = [
        { v: 1, label: 'Enero' }, { v: 2, label: 'Febrero' }, { v: 3, label: 'Marzo' },
        { v: 4, label: 'Abril' }, { v: 5, label: 'Mayo' }, { v: 6, label: 'Junio' },
        { v: 7, label: 'Julio' }, { v: 8, label: 'Agosto' }, { v: 9, label: 'Septiembre' },
        { v: 10, label: 'Octubre' }, { v: 11, label: 'Noviembre' }, { v: 12, label: 'Diciembre' },
    ];
    /** Años del selector de periodo: de 2020 al año siguiente al actual. */
    readonly aniosPeriodo = Array.from(
        { length: new Date().getFullYear() - 2020 + 2 }, (_, i) => 2020 + i,
    );
    /** Suscripción a las subidas de estados de cuenta completadas. */
    private uploadSub?: Subscription;
    /** Mide el tiempo activo en conciliación e informes. */
    private readonly tiempoActivo = inject(TiempoActivoService);

    /**
     * Inyecta los servicios del shell y registra el efecto de conectividad.
     *
     * El efecto ignora su primera ejecución (el estado inicial no es un cambio) y, al recuperar la
     * conexión, reanuda el polling de alertas y renueva el access token si caducó mientras el
     * backend estaba caído y el refresh token sigue vigente.
     * @param router - Router para navegar y escuchar el fin de cada navegación.
     * @param backendService - Servicio de autenticación y salud del backend (lo usa el template).
     * @param activatedRoute - Ruta activa, para leer el título de la hoja más profunda.
     * @param cdr - Detector de cambios; en zoneless se fuerza tras mutar estado plano.
     * @param message - Notificaciones tipo toast.
     * @param alertaService - Contador y resumen de alertas de la campana (lo usa el template).
     * @param uploadTracker - Seguimiento de subidas en curso e interrumpidas (lo usa el template).
     * @param configService - Configuración global del sistema, para el onboarding.
     * @param periodoPendienteService - Estados de cuenta sin periodo, para el modal obligatorio.
     */
    constructor(
        private router: Router,
        public backendService: AuthService,
        private activatedRoute: ActivatedRoute,
        private cdr: ChangeDetectorRef,
        private message: NotificationService,
        public alertaService: AlertaService,
        public uploadTracker: UploadTrackingService,
        private configService: ConfiguracionService,
        private periodoPendienteService: PeriodoPendienteService,
    ) {
        let firstRun = true;
        effect(() => {
            const online = this.backendService.backendOnline();
            if (firstRun) { firstRun = false; return; }

            if (online) {
                this.message.success('Conexión con el servidor restablecida');
                if (this.backendService.isLoggedIn()) {
                    this.alertaService.startPolling();
                    if (this.backendService.isTokenExpired()
                        && this.backendService.getRefreshToken()
                        && !this.backendService.isRefreshTokenExpired()) {
                        this.backendService.refreshToken().subscribe({ error: () => {} });
                    }
                }
            } else {
                this.message.warning('Conexión con el servidor perdida');
            }
        });
    }

    /**
     * Al recuperar la red del navegador, consulta la salud del backend sin esperar al siguiente ciclo del poll.
     */
    @HostListener('window:online')
    onBackOnline(): void {
        this.backendService.checkHealthNow();
    }

    /**
     * Cierra los desplegables de notificaciones y de subidas cuando el clic cae fuera de ellos.
     * @param event - Clic en el documento; su `target` decide qué panel se cierra.
     */
    @HostListener('document:click', ['$event'])
    onDocumentClick(event: Event): void {
        const target = event.target as HTMLElement;
        if (!target.closest('.notification-wrapper')) {
            this.showNotifications = false;
        }
        if (!target.closest('.upload-wrapper')) {
            this.showUploadPanel = false;
        }
    }

    /**
     * Abre o cierra el desplegable de notificaciones y cierra el de subidas (solo uno abierto a la vez).
     * @param event - Clic del botón; se detiene su propagación para que `onDocumentClick` no lo cierre.
     */
    toggleNotifications(event: Event): void {
        event.stopPropagation();
        this.showNotifications = !this.showNotifications;
        this.showUploadPanel = false;
    }

    /**
     * Abre o cierra el panel de subidas y cierra el de notificaciones (solo uno abierto a la vez).
     * @param event - Clic del botón; se detiene su propagación para que `onDocumentClick` no lo cierre.
     */
    toggleUploadPanel(event: Event): void {
        event.stopPropagation();
        this.showUploadPanel = !this.showUploadPanel;
        this.showNotifications = false;
    }

    /**
     * Traduce el origen técnico de una transferencia al nombre del módulo que ve el usuario.
     * @param origin - Módulo que inició la subida o la descarga.
     * @returns Etiqueta legible (facturas, estados de cuenta, SASS, corte de caja, informes o efectivo).
     */
    originLabel(origin: DownloadOrigin): string {
        const map: Record<DownloadOrigin, string> = {
            egresos: 'Facturas',
            conciliacion: 'Estados de cuenta',
            sass: 'SASS',
            ingresos: 'Corte de caja',
            informes: 'Informes',
            efectivo: 'Efectivo',
        };
        return map[origin];
    }

    /**
     * Descarta el aviso de subidas interrumpidas y borra sus migas de `localStorage`.
     */
    dismissOrphaned(): void {
        this.orphanedUploads = [];
        this.uploadTracker.clearAllBreadcrumbs();
    }

    /**
     * Marca una alerta como leída y refresca el contador y la lista de la campana.
     * @param id - Identificador de la alerta.
     */
    markAsRead(id: number): void {
        this.alertaService.marcarLeida(id).subscribe(() => {
            this.alertaService.refreshCount();
            this.alertaService.refreshAlertas();
        });
    }

    /**
     * Marca todas las alertas como leídas y refresca el contador y el resumen por tipo.
     */
    markAllRead(): void {
        this.alertaService.marcarTodasLeidas().subscribe(() => {
            this.alertaService.refreshCount();
            this.alertaService.refreshResumen();
        });
    }

    /**
     * Devuelve la etiqueta legible de un tipo de alerta (fuente única en `core/notifications`).
     * @param tipo - Clave del tipo de alerta que envía el backend.
     * @returns La etiqueta conocida o, si el backend agregó un tipo nuevo, la clave tal cual.
     */
    alertaTipoLabel(tipo: string): string {
        return alertaTipoLabel(tipo);
    }

    /**
     * Cierra la campana y abre el motor de alertas filtrado por un tipo.
     * @param tipo - Tipo de alerta que se pasa como query param `tipo`.
     */
    goToTipoAlerta(tipo: string): void {
        this.showNotifications = false;
        this.router.navigate(['/alertas'], { queryParams: { tipo } });
    }

    /**
     * Arranca el shell: poll de salud, usuario, verificación de subidas huérfanas y polling de alertas.
     *
     * También se suscribe a las subidas de estados de cuenta para reevaluar los periodos pendientes, y a
     * cada `NavigationEnd` para decidir si la ruta va sin shell, actualizar el título, cargar el usuario
     * y reanudar el polling al salir del login. Si la verificación de huérfanas falla, todas se reportan
     * como interrumpidas para no perder el aviso.
     */
    ngOnInit() {
        this.backendService.startHealthPoll();
        this.tiempoActivo.iniciar();
        this.loadUser();

        const orphans = this.uploadTracker.checkBreadcrumbs();
        if (orphans.length > 0) {
            this.uploadTracker.verifyOrphans(orphans).subscribe({
                next: (verified) => {
                    this.orphanedUploads = verified;
                    this.uploadTracker.clearAllBreadcrumbs();
                    this.cdr.detectChanges();
                },
                error: () => {
                    this.orphanedUploads = orphans.map(o => ({
                        origin: o.origin,
                        fileNames: o.fileNames,
                        found: [],
                        missing: o.fileNames,
                        status: 'interrupted' as const,
                    }));
                    this.uploadTracker.clearAllBreadcrumbs();
                    this.cdr.detectChanges();
                },
            });
        }

        if (this.backendService.isLoggedIn()) {
            this.alertaService.startPolling();
        }

        // Tras cada subida de estados de cuenta, reevaluar si algún estado
        // quedó sin mes determinable (modal obligatorio).
        this.uploadSub = this.uploadTracker.onUploadComplete('conciliacion')
            .subscribe(() => this.checkPendientesPeriodo());

        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe((event: any) => {
            const url = event.urlAfterRedirects || event.url;
            // primer-login también va sin shell (pantalla obligatoria a página completa)
            this.isLoginPage = url === '/login' || url === '/' || url.startsWith('/login') || url.startsWith('/primer-login');
            this.updateTitle();

            if (!this.isLoginPage && !this.username) {
                this.loadUser();
            }

            // Start polling when navigating away from login
            if (!this.isLoginPage && this.backendService.isLoggedIn()) {
                this.alertaService.startPolling();
            }
        });
    }

    /**
     * Resuelve el nombre del usuario y dispara los chequeos que dependen de él.
     *
     * Primero toma el nombre del JWT (funciona sin backend) y después lo confirma con `/me`. Si el
     * usuario aún no completó su primer login lo devuelve a esa pantalla obligatoria; si no, revisa el
     * onboarding de configuración y los periodos pendientes. Sin backend o ante error usa "Usuario".
     */
    private loadUser() {
        if (!this.backendService.isLoggedIn()) return;

        const tokenUser = this.backendService.getUsernameFromToken();
        if (tokenUser) {
            this.username = tokenUser;
            if (this.username.includes('@')) {
                this.username = this.username.split('@')[0];
            }
        }

        if (this.backendService.backendOffline()) {
            if (!this.username) this.username = 'Usuario';
            this.cdr.detectChanges();
            return;
        }

        this.backendService.me().subscribe({
            next: (user) => {
                if (user && user.username) {
                    this.username = user.username;
                }
                // Recarga a mitad del setup: volver a la pantalla obligatoria
                if (user?.primer_login) {
                    this.router.navigate(['/primer-login']);
                } else {
                    this.checkConfigSetup();
                    this.checkPendientesPeriodo();
                }
                this.cdr.detectChanges();
            },
            error: () => {
                if (!this.username) this.username = 'Usuario';
                this.cdr.detectChanges();
            }
        });
    }

    /**
     * Toma el título de la ruta hoja (la más anidada), porque el `data.title` vive en la ruta lazy y no en la raíz.
     */
    private updateTitle() {
        let route = this.activatedRoute;
        while (route.firstChild) {
            route = route.firstChild;
        }
        this.pageTitle = route.snapshot.data['title'] || '';
    }

    /**
     * Si es ADMIN y el sistema no está configurado, muestra el onboarding (una vez por sesión).
     *
     * Precarga el formulario con los valores que ya devuelve el backend. Si la consulta falla no se
     * muestra nada, para no bloquear al usuario con un modal por un error transitorio.
     */
    private checkConfigSetup(): void {
        if (this.configChecked || !this.backendService.isAdmin()) return;
        this.configChecked = true;
        this.configService.getConfig().subscribe({
            next: (cfg) => {
                if (!cfg.configurado) {
                    this.cfgMes = cfg.mes_inicio;
                    this.cfgAnio = cfg.anio_inicio;
                    this.cfgSaldo = cfg.saldo_inicial;
                    this.showConfigSetup = true;
                    this.cdr.detectChanges();
                }
            },
            error: () => { /* si falla, no molestamos con el modal */ },
        });
    }

    /**
     * Consulta estados de cuenta sin mes definido; si hay, modal obligatorio.
     *
     * Conserva las selecciones que el usuario ya hizo y solo inicializa las de estados nuevos. Sin
     * sesión o sin backend no consulta, y si la consulta falla no bloquea la app.
     */
    private checkPendientesPeriodo(): void {
        if (!this.backendService.isLoggedIn() || this.backendService.backendOffline()) return;
        this.periodoPendienteService.getPendientes().subscribe({
            next: (pendientes) => {
                this.pendientesPeriodo = pendientes;
                for (const p of pendientes) {
                    if (!this.periodoSel[p.estado_cuenta_id]) {
                        this.periodoSel[p.estado_cuenta_id] = { mes: null, anio: null };
                    }
                }
                this.showPeriodoModal = pendientes.length > 0;
                this.cdr.detectChanges();
            },
            error: () => { /* si falla la consulta, no bloqueamos la app */ },
        });
    }

    /**
     * Asigna el mes y año elegidos a un estado de cuenta pendiente y lo quita del modal.
     *
     * Ignora el clic si falta mes o año o si ya hay un guardado en curso para ese estado. El modal se
     * cierra solo cuando no quedan pendientes; el contador de alertas se refresca porque definir el
     * periodo puede resolver alertas ligadas al estado.
     * @param estadoId - Identificador del estado de cuenta (`estado_cuenta_id`).
     */
    guardarPeriodo(estadoId: number): void {
        const sel = this.periodoSel[estadoId];
        if (!sel || sel.mes == null || sel.anio == null || this.savingPeriodo[estadoId]) return;
        this.savingPeriodo[estadoId] = true;
        // Zoneless: mutar un objeto plano no dispara CD; forzarla para que el
        // botón se deshabilite de inmediato al primer clic (no al responder).
        this.cdr.detectChanges();
        this.periodoPendienteService.definirPeriodo(estadoId, sel.mes, sel.anio).subscribe({
            next: (res) => {
                this.savingPeriodo[estadoId] = false;
                this.message.success(res.message);
                this.pendientesPeriodo = this.pendientesPeriodo.filter(
                    (p) => p.estado_cuenta_id !== estadoId,
                );
                this.showPeriodoModal = this.pendientesPeriodo.length > 0;
                this.alertaService.refreshCount();
                this.cdr.detectChanges();
            },
            error: (err) => {
                this.savingPeriodo[estadoId] = false;
                this.message.error(err?.error?.detail || 'No se pudo definir el periodo.');
                this.cdr.detectChanges();
            },
        });
    }

    /**
     * Cancela la suscripción a las subidas de estados de cuenta.
     */
    ngOnDestroy(): void {
        this.uploadSub?.unsubscribe();
    }

    /**
     * Guarda la configuración inicial del sistema desde el onboarding y cierra el modal si tuvo éxito.
     */
    guardarConfigSetup(): void {
        this.savingConfig = true;
        this.configService.updateConfig({
            mes_inicio: this.cfgMes,
            anio_inicio: this.cfgAnio,
            saldo_inicial: this.cfgSaldo ?? 0,
        }).subscribe({
            next: () => {
                this.savingConfig = false;
                this.showConfigSetup = false;
                this.message.success('Configuración del sistema guardada.');
                this.cdr.detectChanges();
            },
            error: () => {
                this.savingConfig = false;
                this.message.error('No se pudo guardar la configuración.');
                this.cdr.detectChanges();
            },
        });
    }

    /**
     * Abre la confirmación — el cierre real ocurre en confirmLogout().
     */
    logout(): void {
        this.showLogoutConfirm = true;
    }

    /**
     * Cierra la sesión tras la confirmación: limpia el usuario y los tokens y vuelve al login.
     */
    confirmLogout(): void {
        this.showLogoutConfirm = false;
        this.username = '';
        this.backendService.logout();
        this.router.navigate(['/login']);
    }
}
